import PortalCommandDashboard from "../components/PortalCommandDashboard";
import AdminNotificationCount from "../components/AdminNotificationCount";
import MusicIdentityEditor from "../components/MusicIdentityEditor";
import { MusicProfileEditor } from "./MusicOrganizationPortal";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  User,
  Shield,
  Users,
  Key,
  LogOut,
  Settings,
  FileText,
  Calendar,
  CreditCard,
  Download,
  Music,
  Film,
  File,
  Gift,
  BookOpen,
  Library,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import {
  getMinistryLabel,
  getMinistryDescription,
  normalizeMinistryKeys,
  MINISTRY_DEFINITIONS,
} from "../constants/permissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeader() {
  // Auth is now via httpOnly cookie sent automatically with withCredentials.
  return {};
}

export default function UnifiedPortal() {
  const navigate = useNavigate();
  const legacyToolsPage = useLocation().pathname === "/portal/tools";
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [subscriberAssets, setSubscriberAssets] = useState([]);
  const [patronClaim, setPatronClaim] = useState(null);
  const [musicAccess, setMusicAccess] = useState({});
  const [musicProfiles,setMusicProfiles]=useState({});

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/nationals/me`, getAuthHeader());
      const userData = response.data;

      setUser(userData);
      // Music workspaces are assigned independently from YaSHaRUN/Subscriber roles.
      try {
        const access = await axios.get(`${API}/music/access`, getAuthHeader());
        setMusicAccess(access.data?.workspaces || {});
        const areas=Object.keys(access.data?.workspaces||{}).filter(k=>["records","publishing"].includes(k) && access.data.workspaces[k]?.access);
        const results=await Promise.allSettled(areas.map(a=>axios.get(`${API}/music/${a}/me`)));
        setMusicProfiles(Object.fromEntries(areas.map((a,i)=>[a,results[i].status==="fulfilled"?results[i].value.data.profile:null])));
      } catch {
        setMusicAccess({});
      }

      if ((userData.roles || []).includes("subscriber")) {
        try {
          const assetsRes = await axios.get(
            `${API}/subscriber-assets/current`,
            getAuthHeader()
          );
          setSubscriberAssets(assetsRes.data);
        } catch {
          setSubscriberAssets([]);
        }
      }

      if (
        userData.subscription_tier === "patron" &&
        userData.subscription_status === "active"
      ) {
        try {
          const claimRes = await axios.get(
            `${API}/patron/merch-claim`,
            getAuthHeader()
          );
          setPatronClaim(claimRes.data);
        } catch {
          setPatronClaim(null);
        }
      }
    } catch {
      navigate("/nationals/login");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleClaimMerch = async () => {
    try {
      await axios.post(`${API}/patron/merch-claim/claim`, {}, getAuthHeader());

      setPatronClaim((prev) => ({
        ...(prev || {}),
        eligible: true,
        claim_status: "claimed",
      }));

      toast.success(
        "Your Patron merch item has been claimed! We'll be in touch to arrange delivery."
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not claim your merch item");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch {
      // Cookie/session cleanup handled server-side where available.
    }

    toast.success("Logged out successfully");
    navigate("/nationals/login");
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    try {
      await axios.post(
        `${API}/nationals/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        getAuthHeader()
      );

      toast.success("Password changed successfully");
      setShowPasswordModal(false);
      setCurrentPassword("");
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    }
  };

  const handleUsernameChange = async (e) => {
    e.preventDefault();

    try {
      await axios.post(
        `${API}/nationals/change-username`,
        {
          new_username: newUsername,
        },
        getAuthHeader()
      );

      toast.success("Username changed successfully");
      setShowUsernameModal(false);
      setNewUsername("");
      fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change username");
    }
  };

  const handleManageBilling = async () => {
    try {
      const res = await axios.post(`${API}/payments/customer-portal`, {
        origin_url: window.location.origin,
      });

      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
        "No active billing account found yet. Subscribe first to manage billing."
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center">
        <p className="text-slate-600">Loading...</p>
      </div>
    );
  }

  if (!user) return null;

  const roles = user.roles || [];
  const ministries = normalizeMinistryKeys(user.ministries || []);
  const adminScopes = user.admin_scopes || [];

  const managedMinistryKeys = adminScopes.filter((scope) =>
    MINISTRY_DEFINITIONS.some((m) => m.key === scope)
  );

  const isAdmin = roles.includes("admin");
  const canPlanDevotional =
    isAdmin ||
    adminScopes.includes("*") ||
    adminScopes.includes("all_except_super_admin") ||
    ministries.includes("research_discovery_scroll_house") ||
    ministries.includes("communications_media_technology");
  const isNational = roles.includes("national");
  const isSubscriber = roles.includes("subscriber");
  const purchasedItems = user.purchased_items || [];
  const usernameChangesLeft = 2 - (user.username_change_count || 0);

  return (
    <div className="min-h-screen bg-parchment" data-testid="unified-portal-page">
      {/* Header */}
      <section className="bg-royal-purple text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1
                className="font-heading text-xl sm:text-2xl font-bold"
                data-testid="portal-welcome-banner"
              >
                Shalum, {user.display_name}!
              </h1>

              <p className="text-purple-200 mt-1">Your Account Portal</p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Link to="/account/settings" aria-label="Open account settings" className="inline-flex items-center gap-2 rounded border border-white/50 px-3 py-2 text-sm font-semibold text-white hover:bg-white/10"><User size={17} /><span>My Account & Settings</span></Link>
              <Button variant="outline" className="border-white text-white hover:bg-white/10"
                onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </section>

      <PortalCommandDashboard user={user} isAdmin={isAdmin} canPlanDevotional={canPlanDevotional} musicAccess={musicAccess} />


      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-royal-blue">
                Change Password
              </CardTitle>

              <CardDescription>
                Password must be at least 8 characters with 1 uppercase and 1
                special character.
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <Label className="form-label">Current Password</Label>
                  <Input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="form-input"
                    required
                    style={{ fontSize: "16px" }}
                  />
                </div>

                <div>
                  <Label className="form-label">New Password</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="form-input"
                    required
                    style={{ fontSize: "16px" }}
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowPasswordModal(false)}
                  >
                    Cancel
                  </Button>

                  <Button type="submit" className="flex-1 btn-gold">
                    Change Password
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Username Change Modal */}
      {showUsernameModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-royal-blue">
                Change Username
              </CardTitle>

              <CardDescription>
                You can change your username {usernameChangesLeft} more time(s).
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleUsernameChange} className="space-y-4">
                <div>
                  <Label className="form-label">New Username</Label>
                  <Input
                    type="text"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    className="form-input"
                    required
                    style={{ fontSize: "16px" }}
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowUsernameModal(false)}
                  >
                    Cancel
                  </Button>

                  <Button type="submit" className="flex-1 btn-gold">
                    Change Username
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
